'use client';

import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
// Type-only + constant import from a narrow subpath: pulls the consent module
// alone, not the GA loader, so this package does not gain a next/script dependency.
import { CONSENT_CHANGE_EVENT } from '@west-tennessee-consulting/analytics/consent';

import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './styles.css';

/** Consent category keys shared across all apps using this package. */
export const CONSENT_CATEGORY_NECESSARY = 'necessary';
export const CONSENT_CATEGORY_ANALYTICS = 'analytics';

/**
 * Re-opens the preferences modal — wire this to a "Cookie settings" link or
 * button anywhere in the app (e.g. the footer or a cookie-policy page) so a
 * visitor can change their choice without clearing cookies by hand. Thin
 * wrapper so consumers don't need their own direct dependency on
 * vanilla-cookieconsent (a CJS `export =` module, awkward to re-export
 * directly through ESM `export { x } from '...'`).
 */
export function showCookiePreferences(): void {
  CookieConsent.showPreferences();
}

/**
 * Canonical WTC privacy policy. Only the DEFAULT for apps in the WTC family —
 * NOT a generic default for every consumer. Any non-WTC-branded app MUST pass
 * its own `privacyPolicyUrl`; leaving it unset would link visitors to a policy
 * that doesn't describe that app's own data practices.
 */
export const WTC_PRIVACY_POLICY_URL = 'https://www.westtn.consulting/en/privacy';

/**
 * All user-facing banner copy for one language. `{siteName}` in `description`
 * is replaced with the `siteName` prop at render time.
 */
export interface CookieConsentStrings {
  consentModal: {
    title: string;
    description: string;
    acceptAllBtn: string;
    acceptNecessaryBtn: string;
    showPreferencesBtn: string;
    /** Anchor text for the privacy-policy link in the banner footer. */
    privacyPolicyLinkText: string;
  };
  preferencesModal: {
    title: string;
    acceptAllBtn: string;
    acceptNecessaryBtn: string;
    savePreferencesBtn: string;
    closeIconLabel: string;
    necessaryTitle: string;
    necessaryDescription: string;
    analyticsTitle: string;
    analyticsDescription: string;
  };
}

/**
 * Built-in copy, keyed by locale. `en` is the only locale shipped by default —
 * this is the exact copy every consumer got before locale support existed, so
 * an app that passes neither `locale` nor `translations` renders identically
 * to before. Add more locales via the `translations` prop rather than editing
 * this map, so a consumer's translations never collide with a future addition
 * here.
 */
export const DEFAULT_COOKIE_CONSENT_STRINGS: Readonly<Record<string, CookieConsentStrings>> = {
  en: {
    consentModal: {
      title: 'We value your privacy',
      description:
        '{siteName} uses cookies to understand how visitors use the site and to improve your experience. You can accept all cookies or reject the non-essential ones.',
      acceptAllBtn: 'Accept all',
      acceptNecessaryBtn: 'Reject non-essential',
      showPreferencesBtn: 'Manage preferences',
      privacyPolicyLinkText: 'Privacy Policy',
    },
    preferencesModal: {
      title: 'Cookie preferences',
      acceptAllBtn: 'Accept all',
      acceptNecessaryBtn: 'Reject non-essential',
      savePreferencesBtn: 'Save preferences',
      closeIconLabel: 'Close',
      necessaryTitle: 'Strictly necessary cookies',
      necessaryDescription:
        'Required for the site to function (authentication, security, remembering your consent choice). Always on.',
      analyticsTitle: 'Analytics cookies',
      analyticsDescription:
        'Help us understand how the site is used (Google Analytics). No personal profiles are built and data is used only to improve the experience.',
    },
  },
};

export interface CookieConsentBannerProps {
  /** Display name used in the banner copy (e.g. "West Tennessee Consulting"). */
  siteName?: string;
  /**
   * Privacy-policy URL rendered in the banner footer. Defaults to the shared
   * WTC privacy policy ({@link WTC_PRIVACY_POLICY_URL}) — every non-WTC app
   * MUST override this with its own policy URL.
   */
  privacyPolicyUrl?: string;
  /**
   * Language to render. Defaults to `"en"`. Must have a matching entry in
   * {@link DEFAULT_COOKIE_CONSENT_STRINGS} or in the `translations` prop, or
   * this falls back to `"en"`.
   */
  locale?: string;
  /**
   * Additional or overriding copy, keyed by locale — merged OVER
   * {@link DEFAULT_COOKIE_CONSENT_STRINGS}, so a consumer only needs to supply
   * the locales it actually adds (e.g. `{ es: {...}, ja: {...} }`), not the
   * `en` default too.
   */
  translations?: Readonly<Record<string, CookieConsentStrings>>;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Pushes a Google Consent Mode v2 update reflecting the user's current
 * category choices. Safe on apps without Google Analytics: the entry sits
 * in the dataLayer and is simply never read.
 */
function updateGtagConsent(): void {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // gtag must push the IArguments object itself (gtag.js contract).
  function gtag(..._args: unknown[]): void {
    window.dataLayer?.push(arguments);
  }
  const analyticsGranted = CookieConsent.acceptedCategory(CONSENT_CATEGORY_ANALYTICS);
  const value = analyticsGranted ? 'granted' : 'denied';
  gtag('consent', 'update', {
    analytics_storage: value,
    functionality_storage: value,
  });

  // Tell @west-tennessee-consulting/analytics about the decision. Consent Mode alone is not enough: the
  // event layer holds pre-consent events in a queue and needs an explicit signal to
  // release them, otherwise every interaction that happened while this banner was on
  // screen is lost — which is most of the first-touch behavior on a funnel.
  //
  // A DOM event rather than an import keeps the dependency one-way: this package
  // owns consent, @west-tennessee-consulting/analytics owns events, and neither bundles the other's code.
  window.dispatchEvent(
    new CustomEvent(CONSENT_CHANGE_EVENT, { detail: { analytics: value } })
  );
}

function resolveStrings(
  locale: string,
  translations: Readonly<Record<string, CookieConsentStrings>> | undefined
): CookieConsentStrings {
  return (
    translations?.[locale] ??
    DEFAULT_COOKIE_CONSENT_STRINGS[locale] ??
    translations?.en ??
    DEFAULT_COOKIE_CONSENT_STRINGS.en
  );
}

/**
 * Cookie-consent banner, built on vanilla-cookieconsent v3 (MIT) with Google
 * Consent Mode v2 integration. Brand-neutral by default — usable by any app,
 * not just WTC-branded ones.
 *
 * Render once in the app's root layout, inside <body>:
 *
 * ```tsx
 * import { CookieConsentBanner } from '@west-tennessee-consulting/cookie-consent';
 *
 * <body>
 *   <CookieConsentBanner siteName="Acme Co" privacyPolicyUrl="https://acme.example/privacy" />
 *   {children}
 * </body>
 * ```
 *
 * Non-English or multi-locale sites pass `locale` (matching whatever the
 * app's own current-locale value is) and `translations` (any locales beyond
 * the built-in `en`):
 *
 * ```tsx
 * <CookieConsentBanner locale={locale} translations={{ es: {...}, ja: {...} }} />
 * ```
 *
 * Theming: the banner inherits each app's look via CSS variables. The
 * packaged styles.css maps cookie-consent tokens to the app's theme tokens
 * (--background, --foreground, --primary, ...) with neutral fallbacks. Apps
 * whose tokens are not full color values (e.g. raw HSL triples) override the
 * `#cc-main` block in their own globals.css.
 *
 * Consent flow: GA loads with Consent Mode v2 defaults DENIED (see
 * @west-tennessee-consulting/analytics's GoogleAnalytics). Accepting analytics here flips
 * consent to granted; rejecting keeps GA cookieless. Consent choice is
 * persisted by the library in the `cc_cookie` cookie (consent id + ISO
 * timestamps), which is the auditable consent record.
 */
export function CookieConsentBanner({
  siteName = 'This site',
  privacyPolicyUrl = WTC_PRIVACY_POLICY_URL,
  locale = 'en',
  translations,
}: CookieConsentBannerProps) {
  useEffect(() => {
    const s = resolveStrings(locale, translations);
    const hasLocaleEntry = translations?.[locale] !== undefined || DEFAULT_COOKIE_CONSENT_STRINGS[locale] !== undefined;
    const resolvedLocale = hasLocaleEntry ? locale : 'en';

    void CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom right',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
        },
      },
      categories: {
        [CONSENT_CATEGORY_NECESSARY]: {
          enabled: true,
          readOnly: true,
        },
        [CONSENT_CATEGORY_ANALYTICS]: {
          autoClear: {
            cookies: [{ name: /^_ga/ }, { name: '_gid' }],
          },
        },
      },
      language: {
        default: resolvedLocale,
        translations: {
          [resolvedLocale]: {
            consentModal: {
              title: s.consentModal.title,
              description: s.consentModal.description.replace('{siteName}', siteName),
              acceptAllBtn: s.consentModal.acceptAllBtn,
              acceptNecessaryBtn: s.consentModal.acceptNecessaryBtn,
              showPreferencesBtn: s.consentModal.showPreferencesBtn,
              footer: privacyPolicyUrl
                ? `<a href="${privacyPolicyUrl}" target="_blank" rel="noopener noreferrer">${s.consentModal.privacyPolicyLinkText}</a>`
                : undefined,
            },
            preferencesModal: {
              title: s.preferencesModal.title,
              acceptAllBtn: s.preferencesModal.acceptAllBtn,
              acceptNecessaryBtn: s.preferencesModal.acceptNecessaryBtn,
              savePreferencesBtn: s.preferencesModal.savePreferencesBtn,
              closeIconLabel: s.preferencesModal.closeIconLabel,
              sections: [
                {
                  title: s.preferencesModal.necessaryTitle,
                  description: s.preferencesModal.necessaryDescription,
                  linkedCategory: CONSENT_CATEGORY_NECESSARY,
                },
                {
                  title: s.preferencesModal.analyticsTitle,
                  description: s.preferencesModal.analyticsDescription,
                  linkedCategory: CONSENT_CATEGORY_ANALYTICS,
                },
              ],
            },
          },
        },
      },
      onFirstConsent: updateGtagConsent,
      onConsent: updateGtagConsent,
      onChange: updateGtagConsent,
    });
  }, [siteName, privacyPolicyUrl, locale, translations]);

  return null;
}
